<template>
  <b-modal
    id="create-manual-entry"
    ref="create-manual-entry"
    size="atto-modals"
    modal-class="create-manual-entry"
    centered
    no-close-on-backdrop
    no-close-on-esc
    hide-footer
    hide-header
    @hide="onModalHide"
  >
    <div class="modal-head">
      <h2 class="modal-title">
        {{ label.get("new_time_entry") }}
      </h2>
    </div>
    <div id="new-entry-container" class="content-wrapper">
      <perfect-scrollbar>
        <div class="field-row">
          <span class="field-label">{{
            label.get("new_time_entry_entry_type")
          }}</span>
          <div class="field-details">
            <b-form-group>
              <b-form-radio-group
                v-model="entryType"
                :options="options"
                name="radio-options"
              />
            </b-form-group>
          </div>
        </div>
        <div class="field-row">
          <span class="field-label">{{ label.get("date") }}</span>
          <div class="field-details">
            <compact-datepicker-component
              :initial-date="date"
              :entry-type="entryType"
              :class="{ error: errors.date }"
              @onDateSelected="onDateSelected"
            />
            <span v-if="errors.date" class="error-message">{{
              label.get("please_select_date")
            }}</span>
          </div>
        </div>
        <div v-if="entryType === 0" class="entry-details work-hours">
          <div class="field-row">
            <span
              class="field-label clock"
              v-html="label.get('clock_in_out')"
            ></span>
            <div class="field-details">
              <div class="in">
                <time-picker-component
                  v-model="timesheet.clock_in"
                  class="start-time"
                  :class="{ error: errors.start }"
                />
              </div>
              <i class="atto atto-rdir-arrow-b" />
              <div class="out">
                <time-picker-component
                  v-model="timesheet.clock_out"
                  class="end-time"
                  :class="{ error: errors.end }"
                />
              </div>
              <span v-if="errors.start || errors.end" class="error-message">{{
                errors.clockInOutMessage
              }}</span>
            </div>
          </div>
          <div v-if="!isAutomaticBreakEnabled">
            <div
              v-for="(breakItem, idx) in timesheet.breaks"
              :key="'Break' + idx"
              class="field-row mb-8"
            >
              <span class="field-label">{{
                idx === 0 ? label.get("breaks") : ""
              }}</span>
              <div class="field-details" :class="hasBreakError(idx)">
                <select-dropdown-component
                  v-if="isCustomBreakRule"
                  :value="breakItem.break_code_id"
                  :index="idx"
                  :placeholder="label.get('add_break_type')"
                  :options="filteredBreakCodes"
                  :empty-options-hint="label.get('no_break_types')"
                  @onOptionSelected="onBreakCodeSelected"
                />
                <div class="in">
                  <time-picker-component
                    v-model="breakItem.start"
                    class="start-time"
                  />
                </div>
                <i class="atto atto-rdir-arrow-b" />
                <div class="out">
                  <time-picker-component
                    v-model="breakItem.end"
                    class="end-time"
                  />
                </div>
                <button
                  type="reset"
                  class="clear-entry"
                  @click="removeBreak(idx)"
                >
                  <i class="atto atto-close-btn" />
                </button>
              </div>
            </div>
          </div>

          <div v-if="errors.breaks.length > 0" class="field-row">
            <span class="field-label" />
            <span class="error-message">{{ errors.breaks[0].message }}</span>
          </div>
          <div v-if="!isAutomaticBreakEnabled" class="field-row">
            <span class="field-label blank" />
            <div class="field-details">
              <a
                class="add-new-break"
                href="javascript:void(0);"
                @click="addNewBreak"
                >{{ label.get("add_another_break") }}</a
              >
            </div>
          </div>
          <div
            v-for="(jobItem, idx) in timesheet.jobs"
            :key="'Jobs' + idx"
            class="field-row mb-8"
          >
            <span class="field-label">{{
              idx === 0 ? label.get("jobs") : ""
            }}</span>
            <div class="field-details" :class="hasJobError(idx)">
              <select-dropdown-component
                :value="jobItem.job_id"
                :index="idx"
                :placeholder="label.get('add_job_code')"
                :options="filteredJobs"
                empty-options-hint="No Job Codes"
                @onOptionSelected="onJobSelected"
              />
              <div class="in">
                <time-picker-component
                  v-model="jobItem.start"
                  class="start-time"
                />
              </div>
              <i class="atto atto-rdir-arrow-b" />
              <div class="out">
                <time-picker-component v-model="jobItem.end" class="end-time" />
              </div>
              <button type="reset" class="clear-entry" @click="removeJob(idx)">
                <i class="atto atto-close-btn" />
              </button>
            </div>
          </div>
          <div v-if="errors.jobs.length > 0" class="field-row">
            <span class="field-label" />
            <span class="error-message">{{ errors.jobs[0].message }}</span>
          </div>
          <div class="field-row">
            <span class="field-label blank" />
            <div class="field-details">
              <a
                class="add-new-break"
                href="javascript:void(0);"
                @click="addNewJob"
                >{{ label.get("add_another_job") }}</a
              >
            </div>
          </div>
          <div v-if="canAddNote" class="field-row al-top">
            <span class="field-label">{{ label.get("notes") }}</span>
            <div class="field-details">
              <textarea
                v-model="timesheet.note"
                name="notes"
                class="manual-entry-notes"
                :placeholder="label.get('type_your_notes_here')"
              />
            </div>
          </div>
        </div>
        <div v-if="entryType === 1" class="entry-details pto">
          <div class="field-row">
            <span class="field-label">{{ label.get("hours") }}</span>
            <div class="all-day-check check-wrapper">
              <b-form-checkbox v-model="timeOff.all_day">
                {{ label.get("all_day") }}
              </b-form-checkbox>
            </div>
            <div v-show="!timeOff.all_day" class="field-details">
              <div class="in">
                <time-picker-component
                  v-model="timeOff.clock_in"
                  class="start-time"
                  :placeholder="label.get('from')"
                  :class="{ error: errors.start }"
                />
              </div>
              <i class="atto atto-rdir-arrow-b" />
              <div class="out">
                <time-picker-component
                  v-model="timeOff.clock_out"
                  class="end-time"
                  :placeholder="label.get('to')"
                  :class="{ error: errors.end }"
                />
              </div>
              <span
                v-if="errors.start || errors.end"
                class="error-message absolute-error"
                >{{ errors.clockInOutMessage }}</span
              >
            </div>
            <div v-show="timeOff.all_day" class="field-details">
              <div class="in hours-per-day">
                <time-picker-component
                  v-model="timeOff.hours"
                  class="hours"
                  :format24h="true"
                  :placeholder="label.get('hours_per_day')"
                  :class="{ error: errors.timeOff_hours }"
                />
              </div>
              <span
                v-if="errors.timeOff_hours"
                class="error-message absolute-error"
                >{{ label.get("add_time_off_hours") }}</span
              >
            </div>
          </div>
          <div class="field-row">
            <span class="break-line" />
          </div>
          <div class="field-row" :class="{ 'mb-6': !errors.time_off_code }">
            <span class="field-label">{{ label.get("time_off_code") }}</span>
            <div class="field-details">
              <select-dropdown-component
                :value="timeOff.time_off_id"
                :index="0"
                :placeholder="label.get('add_time_off_code')"
                :options="filteredTimeOffs"
                @onOptionSelected="onTimeOffCodeSelected"
              />
            </div>
          </div>
          <div v-if="errors.timeOff_code" class="field-row has-error-pto">
            <span class="field-label blank" />
            <span class="error-message">{{
              label.get("select_time_off_code")
            }}</span>
          </div>
          <div v-if="canAddNote" class="field-row al-top">
            <span class="field-label">{{ label.get("notes") }}</span>
            <div class="field-details">
              <textarea
                v-model="timeOff.note"
                name="notes"
                class="manual-entry-notes"
                :placeholder="label.get('type_your_notes_here')"
              />
            </div>
          </div>
        </div>
      </perfect-scrollbar>
    </div>
    <div class="modal-footer">
      <a
        href="javascript:void(0);"
        class="save-create-new"
        @click="addEntry(false)"
        >{{ label.get("add_this_another") }}</a
      >
      <button type="reset" class="atto-btn cancel" @click="cancelEntry">
        {{ label.get("cancel") }}
      </button>
      <button type="submit" class="atto-btn submit" @click="addEntry">
        {{ label.get("add_entry") }}
      </button>
    </div>
    <transition name="fade">
      <div v-if="hasOverlaps" class="overlap-entries-modal">
        <div class="entries-wrapper">
          <div class="header">
            <h2 class="modal-title">
              Time Entry Overlap!
            </h2>
            <h4 class="alert-message">
              <i class="atto atto-alert-triangle" />
              {{
                label.get("there_is_time_overlap_with_an_existing_time_entry")
              }}
            </h4>
          </div>
          <div class="container-wrapper">
            <div
              v-for="(entry, idx) in timesheetOverlaps"
              :key="idx"
              class="field-row"
            >
              <div class="field-details overlap">
                <div class="day">
                  <i class="atto atto-alert-circled" />
                  <p class="name">
                    {{ entry.date }}
                  </p>
                </div>
                <div v-if="!entry.is_time_off" class="time-interval">
                  <span class="start-time">
                    {{ entry.start_time }}
                  </span>
                  <i class="atto atto-rdir-arrow-b" />
                  <span class="end-time">
                    {{ entry.end_time }}
                  </span>
                </div>
                <div v-if="entry.is_time_off" class="pto-msg">
                  <span>
                    {{ entry.name }}
                  </span>
                </div>
                <button
                  v-if="!entry.deleting"
                  class="delete-entry"
                  @click="deleteOverlap(entry)"
                >
                  <i class="atto atto-delete" />
                </button>
                <transition v-if="!entry.deleting" name="fade">
                  <div v-if="entry.confirm_delete" class="confirm-delete">
                    <span class="confirm-msg">
                      {{ label.get("are_you_sure") }}
                    </span>
                    <button
                      class="confirm-delete-btn"
                      @click="confirmDeleteOverlap(entry)"
                    >
                      {{ label.get("delete") }}
                    </button>
                    <button
                      class="cancel-delete"
                      @click="cancelDeleteOverlap(entry)"
                    >
                      <i class="atto atto-close-btn" />
                    </button>
                  </div>
                </transition>
              </div>
            </div>
          </div>
          <div class="overlap-footer">
            <button
              type="reset"
              class="atto-btn close"
              @click="hasOverlaps = false"
            >
              {{ label.get("close") }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </b-modal>
</template>

<script>
import SelectDropdownComponent from "../../../shared/components/SelectDropdownComponent";
import CompactDatepickerComponent from "../components/CompactDatepickerComponent";
import TimePickerComponent from "./components/TimePickerComponent";
import TimesheetService from "../shared/TimesheetService";
import UtilsService from "../../../shared/services/UtilsService";
import TimeEntryValidator from "../shared/services/TimeEntryValidator";

export default {
  name: "CreateManualEntryComponent",
  components: {
    SelectDropdownComponent,
    CompactDatepickerComponent,
    TimePickerComponent,
  },
  props: {
    member: {
      type: Object,
      default: () => {
        return {};
      },
    },
    date: {
      type: String,
      default: "",
    },
    hasScroll: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return this.getInitialModel();
  },
  computed: {
    selectedDate() {
      return this.dates;
    },
    filteredJobs() {
      return this.jobs.map((item) => {
        return {
          value: item.id,
          label: item.name,
        };
      });
    },
    filteredBreakCodes() {
      return this.breakCodes.map((item) => {
        return {
          value: item.id,
          label: item.name,
        };
      });
    },
    filteredTimeOffs() {
      return this.timeOffs.map((item) => {
        return {
          value: item.id,
          label: item.name,
        };
      });
    },
  },
  mounted() {
    this.$refs["create-manual-entry"].show();
  },
  methods: {
    isDateInFuture() {
      return this.date && this.$moment(this.date) > this.$moment();
    },
    addNewBreak() {
      let emptyBreaks = false;
      this.timesheet.breaks.forEach((item) => {
        if (
          item.start === null ||
          item.start === "" ||
          item.end === null ||
          item.end === ""
        ) {
          emptyBreaks = true;
        }
      });

      if (emptyBreaks) {
        return;
      }

      this.timesheet.breaks.push({
        //local_id: UtilsService.getUUID(),
        break_code_id: null,
        start: null,
        end: null,
        deleted: false,
      });
    },
    addNewJob() {
      let emptyJobs = false;
      this.timesheet.jobs.forEach((item) => {
        if (
          item.job_id === null ||
          item.start === null ||
          item.start === "" ||
          item.end === null ||
          item.end === ""
        ) {
          emptyJobs = true;
        }
      });

      if (emptyJobs) {
        return;
      }

      this.timesheet.jobs.push({
        job_id: null,
        //local_id: UtilsService.getUUID(),
        start: null,
        end: null,
        deleted: false,
      });
    },
    removeBreak(index) {
      this.timesheet.breaks.splice(index, 1);
      if (this.timesheet.breaks.length === 0) {
        this.addNewBreak();
      }
      this.errors.breaks = [];
    },
    removeJob(index) {
      this.timesheet.jobs.splice(index, 1);
      if (this.timesheet.jobs.length === 0) {
        this.addNewJob();
      }
      this.errors.jobs = [];
    },
    onDateSelected(date) {
      debugger;
      if (date === undefined || date === null) {
        this.timesheet.dates = [];
        return;
      }
      if (this.entryType === 0) {
        this.timesheet.dates = date.split(",").map(function(item) {
          return item.trim();
        });
      } else {
        this.timeOff.dates = date.split(",").map(function(item) {
          return item.trim();
        });
      }
    },
    onJobSelected(option, idx) {
      this.timesheet.jobs[idx].job_id = option.value;
    },
    onBreakCodeSelected(option, idx) {
      this.timesheet.breaks[idx].break_code_id = option.value;
    },
    onTimeOffCodeSelected(option) {
      this.timeOff.time_off_id = option.value;
    },
    addEntry(closeForm = true) {
      debugger;
      const _this = this;
      this.resetErrors();
      let model = this.entryType === 0 ? this.timesheet : this.timeOff;

      if (!TimeEntryValidator.isValidDate(model)) {
        this.errors.date = true;
        return;
      }

      if (this.entryType === 0) {
        if (!TimeEntryValidator.isValidClockIn(model)) {
          this.errors.start = true;
          this.errors.clockInOutMessage = _this.label.get(
            "message_please_fill_the_required_fields"
          );
          return;
        } else if (!TimeEntryValidator.isValidClockOut(model)) {
          this.errors.end = true;
          this.errors.clockInOutMessage = _this.label.get(
            "message_please_fill_the_required_fields"
          );
          return;
        } else if (TimeEntryValidator.isInvalidClockInOut(model)) {
          this.errors.start = true;
          this.errors.end = true;
          this.errors.clockInOutMessage = TimeEntryValidator.isInvalidClockInOut(
            model
          );
          return;
        } else if (TimeEntryValidator.areBreaksEmpty(model)) {
          const error = TimeEntryValidator.areBreaksEmpty(model);
          this.errors.breaks.push({
            index: error.index,
            message: error.message,
          });
          return;
        } else if (TimeEntryValidator.areJobsEmpty(model)) {
          const error = TimeEntryValidator.areJobsEmpty(model);
          this.errors.jobs.push({
            index: error.index,
            message: error.message,
          });
          return;
        } else if (TimeEntryValidator.areJobCodesSelected(model)) {
          const error = TimeEntryValidator.areJobCodesSelected(model);
          this.errors.jobs.push({
            index: error.index,
            message: error.message,
          });
          return;
        } else if (
          this.isCustomBreakRule &&
          TimeEntryValidator.areBreakCodesSelected(model)
        ) {
          const error = TimeEntryValidator.areBreakCodesSelected(model);
          this.errors.breaks.push({
            index: error.index,
            message: error.message,
          });
          return;
        }

        const data = UtilsService.copyObject(model, true);
        _this.clearEmptyBreaksAndJobs(data);
        debugger;
        _this.$store.commit("showLoading");
        TimesheetService.newTimeEntry(data)
          .then((result) => {
            debugger;
            if (closeForm) {
              _this.$refs["create-manual-entry"].hide();
              this.$root.$emit("hideCreateEntry");
            }
            this.$root.$emit("reloadData");
            this.timesheet = this.initialTimesheetObject();
            _this.$store.commit("hideLoading");
          })
          .catch((error) => {
            debugger;
            console.log(error);
            this.$store.commit("hideLoading");
            if (error.response.status === 422) {
              const { breaks, jobs, entries } = error.response.data;
              if (breaks) {
                this.errors.breaks.push({
                  index: 0,
                  message: _this.label.get(
                    "there_is_time_overlap_in_your_break_hours"
                  ),
                });
              }
              if (jobs) {
                this.errors.jobs.push({
                  index: 0,
                  message: _this.label.get(
                    "there_is_time_overlap_in_your_job_hours"
                  ),
                });
              }
              if (entries) {
                this.fillTimeEntryOverlaps(entries);
              }
            }
          });
      } else {
        if (!model.all_day) {
          if (!TimeEntryValidator.isValidClockIn(model)) {
            this.errors.start = true;
            this.errors.clockInOutMessage = this.label.get(
              "message_please_fill_the_required_fields"
            );
            return;
          }
          if (!TimeEntryValidator.isValidClockOut(model)) {
            this.errors.end = true;
            this.errors.clockInOutMessage = this.label.get(
              "message_please_fill_the_required_fields"
            );
            return;
          }
        } else {
          if (!TimeEntryValidator.isValidTimeOffHours(model)) {
            this.errors.timeOff_hours = true;
            return;
          }
        }
        if (!TimeEntryValidator.hasTimeOffCode(model)) {
          this.errors.timeOff_code = true;
          return;
        }
        debugger;
        _this.$store.commit("showLoading");
        TimesheetService.newTimeOffEntry(model)
          .then((result) => {
            debugger;
            if (closeForm) {
              _this.$refs["create-manual-entry"].hide();
              this.$root.$emit("hideCreateEntry");
            }
            this.$root.$emit("reloadData");
            this.timeOff = this.initialTimeOffObject();
            _this.$store.commit("hideLoading");
          })
          .catch((error) => {
            debugger;
            _this.$store.commit("hideLoading");
            if (error.response.status === 422) {
              const { breaks, jobs, entries } = error.response.data;
              if (breaks) {
                this.errors.breaks.push({
                  index: 0,
                  message: _this.label.get(
                    "there_is_time_overlap_in_your_break_hours"
                  ),
                });
              }
              if (jobs) {
                this.errors.jobs.push({
                  index: 0,
                  message: _this.label.get(
                    "there_is_time_overlap_in_your_job_hours"
                  ),
                });
              }
              if (entries) {
                this.fillTimeEntryOverlaps(entries);
              }
            }
          });
      }
    },
    fillTimeEntryOverlaps(entries) {
      this.timesheetOverlaps = entries.map((item) => {
        debugger;
        const [date, time] = item.label.split("·");
        const [start, end] = time.split("-");

        item.date = date.trim();
        if (item.is_time_off) {
          item.name = time.trim();
        } else {
          item.start_time = start.trim();
          item.end_time = end.trim();
        }
        item.confirm_delete = false;
        item.deleting = false;
        return item;
      });
      this.hasOverlaps = true;
    },
    deleteOverlap(entry) {
      entry.confirm_delete = true;
    },
    confirmDeleteOverlap(entry) {
      let _this = this;
      entry.deleting = true;
      /*let data = {
                    id: entry.id,
                    user_id: this.member.id,
                    start: entry.start,
                    end: entry.end,
                    auto_start: entry.auto_start,
                    auto_end: entry.auto_end
                };*/

      //if (entry.time_off_id === null) {
      TimesheetService.deleteEntry(entry.id)
        .then((result) => {
          let entryIndex = _this.timesheetOverlaps.indexOf(entry);
          _this.timesheetOverlaps.splice(entryIndex, 1);
          if (_this.timesheetOverlaps.length === 0) {
            _this.hasOverlaps = false;
            this.$root.$emit("reloadData");
          }
        })
        .catch((error) => {
          entry.deleting = false;
        });
      /*} else {
                    TimesheetService.deletePTOEntry(data).then((result) => {
                        let entryIndex = _this.timesheetOverlaps.indexOf(entry);
                        _this.timesheetOverlaps.splice(entryIndex, 1);
                        if (_this.timesheetOverlaps.length === 0) {
                            _this.hasOverlaps = false;
                            this.$root.$emit('reloadData');
                        }
                    }).catch((error) => {
                        entry.deleting = false;
                    });
                }*/
    },
    cancelDeleteOverlap(entry) {
      entry.confirm_delete = false;
    },
    cancelEntry() {
      this.timesheet = null;
      this.timeOff = null;
      this.errors = null;
      this.$root.$emit("hideCreateEntry");
      this.$refs["create-manual-entry"].hide();
    },

    hasBreakError(index) {
      let error = this.errors.breaks.filter((item) => item.index === index);
      if (error.length > 0) {
        return "has-error";
      }
      return "";
    },

    hasJobError(index) {
      let error = this.errors.jobs.filter((item) => item.index === index);
      if (error.length > 0) {
        return "has-error";
      }
      return "";
    },

    clearEmptyBreaksAndJobs(model) {
      let breaks = model.breaks;
      let jobs = model.jobs;
      for (let i = 0; i < breaks.length; i++) {
        if (breaks[i].start === "" || breaks[i].start === null) {
          model.breaks.splice(i, 1);
        } else if (breaks[i].end === "" || breaks[i].end === null) {
          model.breaks.splice(i, 1);
        }
      }
      for (let i = 0; i < jobs.length; i++) {
        if (jobs[i].start === "" || jobs[i].start === null) {
          model.jobs.splice(i, 1);
        } else if (jobs[i].end === "" || jobs[i].end === null) {
          model.jobs.splice(i, 1);
        }
      }
    },

    resetErrors() {
      this.errors = {
        date: false,
        start: false,
        end: false,
        clockInOutMessage: null,
        breaks: [],
        jobs: [],
        timeOff_hours: false,
        timeOff_code: false,
      };
    },

    onModalHide() {
      this.$root.$emit("hideCreateEntry");
    },

    getInitialModel() {
      return {
        entryType: this.isDateInFuture() ? 1 : 0,
        isSelectActive: false,
        options: [
          {
            text: this.label.get("work_hours"),
            value: 0,
            disabled: this.isDateInFuture(),
          },
          {
            text: this.label.get("time_off"),
            value: 1,
          },
        ],
        jobs: this.$store.state.companyJobs,
        breakCodes: this.$store.state.breakCodes,
        timeOffs: this.$store.state.companyPto,
        timesheet: this.initialTimesheetObject(),
        timeOff: this.initialTimeOffObject(),
        canAddNote:
          this.member.id === this.$store.state.user.id ||
          this.$store.state.user.role_id === 1,
        errors: {
          date: false,
          start: false,
          end: false,
          clockInOutMessage: null,
          breaks: [],
          jobs: [],
          timeOff_hours: false,
          timeOff_code: false,
        },
        hasOverlaps: false,
        timesheetOverlaps: [],
        isAutomaticBreakEnabled:
          this.$store.state.companySettings.break_rule === 2,
        isCustomBreakRule: this.$store.state.companySettings.break_rule === 3,
      };
    },
    initialTimesheetObject() {
      return {
        user_id: this.member.id,
        //local_id: UtilsService.getUUID(),
        dates: this.date ? [this.date] : [],
        clock_in: null,
        clock_out: null,
        note: null,
        jobs: [
          {
            job_id: null,
            //local_id: UtilsService.getUUID(),
            start: null,
            end: null,
            deleted: false,
          },
        ],
        breaks: [
          {
            //local_id: UtilsService.getUUID(),
            break_code_id: null,
            start: null,
            end: null,
            deleted: false,
          },
        ],
      };
    },
    initialTimeOffObject() {
      return {
        user_id: this.member.id,
        dates: this.date ? [this.date] : [],
        clock_in: null,
        clock_out: null,
        all_day: false,
        hours: null,
        time_off_id: null,
        note: null,
      };
    },
    timeFormat() {
      return this.$store.state.userSettings.is_am_pm_time_format
        ? "h:mma"
        : "HH:mm";
    },
  },
};
</script>

<style lang="scss">
@mixin tabletAndMobile {
  @media (max-width: 767px) {
    @content;
  }
}
.modal.create-manual-entry {
  .modal-atto-modals {
    max-width: 660px;
    max-height: 640px;
    position: relative;

    .content-wrapper {
      padding-right: 0 !important;

      > .ps {
        max-height: 70vh;
        /*max-height: calc(100vh - 190px);*/
        overflow: visible !important;

        .ps__rail-y {
          width: 16px;
          opacity: 1;
          z-index: 2;
          right: 6px !important;

          &:hover {
            background-color: transparent;

            .ps__thumb-y {
              width: 8px;
              background-color: rgba(239, 239, 239, 1);
            }
          }

          &.ps--clicking {
            background-color: transparent;
          }
        }

        .ps__thumb-y {
          right: 4px;
          width: 8px;
          opacity: 1;
          background-color: rgba(239, 239, 239, 0.5);
        }

        &.ps--active-y {
          overflow: hidden !important;
        }
      }

      /*.no-ps{*/
      /*  max-height: 70vh;*/
      /*  overflow-x: hidden;*/
      /*  overflow-y: scroll;*/

      /*  &::-webkit-scrollbar-track*/
      /*  {*/
      /*    !*-webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3);*!*/
      /*    border-radius: 0;*/
      /*    background-color: transparent;*/
      /*  }*/

      /*  &::-webkit-scrollbar*/
      /*  {*/
      /*    width: 16px;*/
      /*    background-color: transparent;*/
      /*    padding: 0 4px;*/
      /*  }*/

      /*  &::-webkit-scrollbar-thumb*/
      /*  {*/
      /*    !*-webkit-box-shadow: inset 0 0 6px rgba(0,0,0,.3);*!*/
      /*    transition: background-color .2s linear, width .2s ease-in-out;*/
      /*    -webkit-transition: background-color .2s linear, width .2s ease-in-out;*/
      /*    width: 8px;*/
      /*    background-color: #efefef;*/
      /*    border-radius: 6px;*/
      /*  }*/
      /*}*/

      .atto-select {
        margin-right: 24px;

        .select-heading {
          width: 192px;
          span {
            font-weight: 500;
          }
        }
      }
    }
  }

  .modal-head {
    padding: 48px 48px 16px;
    @include tabletAndMobile {
      padding: 20px 20px 26px !important;
    }
    h2.modal-title {
      font-family: $roboto-font;
      font-weight: $roboto-medium;
      font-size: 1.25rem;
      line-height: 1.75rem;
      color: $atto-font-color;
    }
  }

  .modal-content .content-wrapper > .ps {
    padding-right: 48px !important;
    @include tabletAndMobile() {
      padding-right: 20px !important;
    }
  }

  .modal-content {
    .modal-body {
      padding: 0 !important;
      .content-wrapper {
        padding: 8px 48px 24px 48px;
        @include tabletAndMobile() {
          padding: 8px 20px 20px 20px;
        }
        .field-row {
          display: flex;
          flex-direction: row;
          margin-bottom: 12px;
          align-items: center;

          .field-label {
            min-width: 100px;
            margin-right: 10px;
            font-size: 0.875rem;
            line-height: 1.5rem;
            font-family: $roboto-font;
            font-weight: $roboto-regular;
            color: $atto-font-color;

            &:not(.blank) {
              height: 40px;
              line-height: 40px;
              align-self: flex-start;
            }
          }

          .all-day-check {
            padding: 0 10px 0 0;
            min-width: 112px;

            .custom-checkbox {
              label {
                padding-left: 8px;
              }
            }
          }

          .field-details {
            display: inline-flex;
            flex-grow: 1;
            align-items: center;
            flex-wrap: wrap;
            margin-bottom: 0;

            fieldset {
              margin-bottom: 0;
            }

            div.input-date,
            > input {
              border: 2px solid $atto-light-grey;
              padding: 8px 12px;
              font-size: 0.875rem;
              line-height: 1.25rem;
              font-family: $roboto-font;
              font-weight: $roboto-regular;
              color: $atto-font-color;
              max-width: 184px;
            }

            input.timepicker {
              border: 2px solid $atto-light-grey;
              padding: 8px;
              font-size: 0.875rem;
              line-height: 1.25rem;
              font-family: $roboto-font;
              font-weight: 500;
              color: $atto-font-color;
              max-width: 184px;
            }

            textarea:focus,
            input:not([type="checkbox"]):focus {
              border-color: $atto-teal;
            }

            input.timepicker {
              max-width: 84px;
              @include style-placeholder(#999);
            }

            i {
              color: $atto-dark-grey;
            }

            i.atto-rdir-arrow-b {
              font-size: 8px;
              color: #c7c7c7;
              margin: 0 8px;
            }

            button.clear-entry {
              margin-left: 4px;
            }

            textarea {
              padding: 8px 12px;
              width: 100%;
              min-height: 80px;
              border: 2px solid $atto-light-grey;
              font-family: $roboto-font;
              font-weight: $roboto-medium;
              font-size: 0.875rem;
              line-height: 1.25rem;
              resize: none;
              outline: $atto-light-grey;
              @include style-placeholder($atto-not-active);
            }

            .error-message.absolute-error {
              position: absolute;
              margin-top: 35px;
              margin-left: 0;
              width: auto;
            }
            .hours-per-day {
              width: 100%;

              input.timepicker {
                width: 114px;
                max-width: 114px;
                height: 40px;
                border: 2px solid #efefef;
                padding: 8px 10px;

                &::-webkit-inner-spin-button,
                &::-webkit-outer-spin-button {
                  -webkit-appearance: none;
                  /* margin: 0;*/
                }
                &::-webkit-input-placeholder {
                  /* Chrome/Opera/Safari */
                  color: #999999;
                  font: $roboto-medium 0.875rem/1.25rem $roboto-font;
                }
              }
            }
          }

          .add-new-break {
            font-size: 0.75rem;
            line-height: 1.5rem;
            color: $atto-not-active;
            font-family: $roboto-font;
            font-weight: $roboto-medium;
            margin-left: 10px;
            @include base-transition(ease color 0.1s);

            &:hover {
              color: $atto-font-color;
            }
          }

          &.mb-8 {
            margin-bottom: 8px;
          }

          .custom-radio {
            cursor: pointer;

            .custom-control {
              align-items: center;
              padding-left: 32px;
            }

            .custom-control-input {
              ~ .custom-control-label {
                &::before,
                &::after {
                  cursor: pointer;
                }
              }
            }

            .custom-control-label {
              /*display: inline-flex;*/

              span {
                font-size: 0.875rem;
                line-height: 1.5rem;
                font-family: $roboto-font;
                font-weight: $roboto-medium;
                color: $atto-font-color;
              }

              &::before {
                width: 24px;
                height: 24px;
                top: 0;
                left: -32px;
                background-color: transparent;
                border: 1.5px solid #c7c7c7;
              }

              &::after {
                width: 12px;
                height: 12px;
                border-radius: 12px;
                left: -26px;
                top: 6px;
                background-color: transparent;
                background-image: none;
              }
            }
          }

          .custom-radio {
            .custom-control-input:checked {
              ~ .custom-control-label {
                &::before {
                  border-color: #00b9cb;
                }

                &::after {
                  width: 12px;
                  height: 12px;
                  border-radius: 12px;
                  left: -26px;
                  top: 6px;
                  background-color: #00b9cb;
                  background-image: none;
                }
              }
            }

            .custom-control-input:disabled ~ .custom-control-label {
              cursor: not-allowed;
            }

            .custom-control-input:disabled ~ .custom-control-label span {
              color: #999;
            }

            .custom-control-input:disabled ~ .custom-control-label:before {
              background-color: #efefef;
              border-color: #c7c7c7;
              cursor: not-allowed;
            }

            .custom-control-input:focus ~ .custom-control-label::before {
              box-shadow: none;
            }
          }

          &.al-top {
            align-items: flex-start;

            .field-label {
              margin-top: 8px;
            }
          }

          .break-line {
            margin: 35px 0 35px 110px;
            height: 2px;
            width: 32px;
            background-color: #efefef;

            &.error {
              margin-top: 3px;
            }
          }

          &.mb-6 {
            margin-bottom: 44px;
          }

          &.has-error {
            margin-bottom: 4px;

            .error-message {
              margin-top: 4px;
            }

            .error-message.pto {
              margin-top: 0;
            }

            .flatpickr-mask,
            input.timepicker,
            input {
              border-color: #e05656;
            }
          }

          &.has-error-pto {
            position: absolute;
            margin-top: -43px;

            .error-message-pto {
              margin-top: 0;
            }
          }
        }
      }
    }
  }

  .modal-footer {
    padding: 0 48px 48px;
    flex-direction: row;
    @include tabletAndMobile() {
      padding: 0 20px 20px !important;
      flex-direction: column;
    }
    justify-content: space-between;
    .atto-btn {
      width: 160px !important;
      @include tabletAndMobile() {
        width: 100% !important;
      }
      cursor: pointer;
      font-family: $roboto-font;
      font-weight: 500;
      text-transform: uppercase;
    }
  }

  .overlap-entries-modal {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    .entries-wrapper {
      width: 480px;
      background-color: #fff;
      position: relative;
      z-index: 2;

      .header {
        display: flex;
        flex-direction: column;
        padding: 30px 30px 24px;

        h2 {
          margin-bottom: 16px;
          font-family: $roboto-font;
          font-weight: $roboto-medium;
          font-size: 1.25rem;
          line-height: 1.75rem;
          color: $atto-font-color;
        }

        h4 {
          font-size: 0.875rem;
          line-height: 1.5rem;
          font-family: "Roboto", sans-serif;
          font-weight: 400;
          color: #262626;
          display: flex;
          align-items: center;
          flex-direction: row;

          i {
            font-size: 17px;
            margin-right: 11px;
          }
        }
      }

      .container-wrapper {
        height: auto;
        padding: 0 32px 24px;

        button.delete-entry {
          margin: 0;
          padding: 0;
          margin-left: 24px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;

          i {
            font-size: 24px;
            color: #efefef;
            @include base-transition(ease color 0.15s);
          }

          &:hover {
            i {
              color: $atto-not-active;
            }
          }
        }

        .field-row {
          display: flex;
          flex-direction: row;
          margin-bottom: 12px;

          .field-details {
            display: inline-flex;
            flex-grow: 1;
            align-items: center;
            position: relative;

            .day {
              display: flex;
              flex-direction: row;
              align-items: center;

              i {
                font-size: 8px;
                width: 12px;
                height: 12px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
              }

              p {
                margin-bottom: 0;
                font-size: 0.875rem;
                line-height: 1.5rem;
                font-family: "Roboto", sans-serif;
                font-weight: 500;
                color: #262626;
                margin-left: 15px;
              }
            }

            .time-interval {
              margin-left: auto;
              display: flex;
              flex-direction: row;
              width: 184px;
              align-items: center;

              .start-time,
              .end-time {
                min-width: 80px;
                max-width: 80px;
                padding: 8px;
                background-color: #f7f7f7;
                text-align: center;
                display: inline-block;

                /* Font Styles */
                font-size: 0.875rem;
                line-height: 1.5rem;
                font-family: "Roboto", sans-serif;
                font-weight: 500;
                color: #262626;
              }

              i {
                font-size: 8px;
                color: #c7c7c7;
                margin: 0 8px;
              }
            }

            .custom-control {
              align-items: center;
              padding-left: 32px;

              &:hover {
                cursor: pointer;
              }
            }

            .custom-control-label {
              display: inline-flex;
              font-size: 0.875rem;
              line-height: 1.5rem;
              font-family: "Roboto", sans-serif;
              font-weight: 500;
              color: #262626;

              &:before {
                content: "s";
                width: 24px;
                height: 24px;
                top: 0;
                left: -32px;
                background-color: transparent;
                border: 1.5px solid #c7c7c7;

                &:hover {
                  cursor: pointer;
                }
              }

              &:after {
                content: "s";
                width: 12px;
                height: 12px;
                border-radius: 12px;
                left: -26px;
                top: 6px;
                background-color: transparent;
                background-image: none;
              }
            }

            .custom-radio {
              .custom-control-input:checked {
                ~ .custom-control-label {
                  &::before {
                    content: "";
                    border-color: #00b9cb;
                    background-color: transparent;
                  }

                  &::after {
                    content: "";
                    width: 12px;
                    height: 12px;
                    border-radius: 12px;
                    left: -26px;
                    top: 6px;
                    background-color: #00b9cb;
                    background-image: none;
                  }
                }
              }

              .custom-control-input:focus ~ .custom-control-label::before {
                box-shadow: none;
              }

              .custom-control-label {
                cursor: pointer;
              }
            }

            &.overlap {
              .day {
                i {
                  font-size: 12px;
                }

                p {
                  color: #e05656;
                }
              }

              .time-interval {
                .start-time,
                .end-time {
                  color: #e05656;
                }
              }
            }

            .confirm-delete {
              position: absolute;
              right: 0;
              top: 0;
              background-color: #fff;
              width: 232px;
              height: 40px;
              display: flex;
              align-items: center;
              flex-direction: row;
              justify-content: flex-end;
              padding-left: 34px;

              .confirm-msg {
                font-size: 0.875rem;
                line-height: 1.75rem;
                font-family: "Roboto", sans-serif;
                font-weight: 400;
                color: #262626;
              }

              .confirm-delete-btn {
                font-size: 0.875rem;
                line-height: 1.75rem;
                font-family: "Roboto", sans-serif;
                font-weight: 500;
                color: #262626;
                margin-left: 12px;
                padding: 0;
                cursor: pointer;
              }

              .cancel-delete {
                width: 24px;
                height: 24px;
                display: flex;
                justify-content: center;
                align-items: center;
                margin-left: 24px;
                padding: 0;
                cursor: pointer;

                i {
                  font-size: 14px;
                  color: #c7c7c7;
                  @include base-transition(ease color 0.15s);
                }

                &:hover {
                  i {
                    color: $atto-not-active;
                  }
                }
              }
            }

            .pto-msg {
              width: 184px;
              margin-left: auto;
              height: 40px;
              display: flex;
              align-items: center;

              span {
                color: $atto-red;
                font-size: 0.875rem;
                line-height: 1.5rem;
                font-family: "Roboto", sans-serif;
                font-weight: 500;
              }
            }
          }
        }
      }

      .overlap-footer {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        padding: 0 30px 24px;

        &:before {
          content: "";
          display: inline-flex;
          width: 100%;
          height: 2px;
          background-color: #efefef;
        }

        .atto-btn.close {
          margin-left: auto;
          margin-top: 8px;
          font-size: 1.125rem;
          line-height: 1.5rem;
          font-family: "Roboto", sans-serif;
          font-weight: 500;
          color: #00b9cb;
          text-align: right;
          width: 100px;
          justify-content: flex-end;
          text-transform: none;
          opacity: 1;
        }
      }
    }

    > &::before,
    > &:before {
      content: "";
      width: 100%;
      height: 100%;
      position: absolute;
      z-index: 1;
      left: 0;
      top: 0;
      background: rgba(0, 0, 0, 0.25);
    }
  }
}
</style>
